/**
 * Component glyphs.
 *
 * Each glyph draws in local coordinates centred on the origin, filling a box of
 * the symbol's declared size; the caller translates and rotates it. Glyph names
 * come from the template's `symbol.glyph` (design §1.6), so a template author
 * chooses the glyph and this file is the only place it is drawn.
 *
 * Symbols are schematic conventions, not geometry: nothing here is to scale.
 */
import type { ReactElement } from "react";
import type { Size } from "../scene/types";
import { THEME } from "./theme";

interface GlyphProps {
  size: Size;
  /**
   * Where the symbol's ports are, in its own unrotated frame. Given for a
   * junction, whose stubs run to its ports: the template, not the glyph, says
   * which side the branch is on.
   */
  ports?: readonly { x: number; y: number }[];
  /** Showing a fill level of its own (D61): a vessel leaves out its drawn level. */
  filled?: boolean;
  /**
   * Flipped by the caller's transform. A letter is mirrored back, so it reads
   * forwards whichever way round the symbol is; it still turns with it.
   */
  mirrored?: boolean;
}

/** Undoes the caller's mirror for a letter centred on x = 0. */
function readable(mirrored: boolean | undefined): string | undefined {
  return mirrored ? "scale(-1 1)" : undefined;
}

/** Coordinates to three decimals, so paths carry no floating-point noise. */
function fmt(value: number): number {
  return Math.round(value * 1000) / 1000;
}

const body = {
  fill: THEME.surface,
  stroke: THEME.ink,
  strokeWidth: THEME.strokeWidth,
} as const;

const detail = {
  fill: "none",
  stroke: THEME.ink,
  strokeWidth: 1,
} as const;

function Box({ size, rx = 2, dashed = false }: GlyphProps & { rx?: number; dashed?: boolean }) {
  return (
    <rect
      x={-size.width / 2}
      y={-size.height / 2}
      width={size.width}
      height={size.height}
      rx={rx}
      {...body}
      strokeDasharray={dashed ? "4 3" : undefined}
    />
  );
}

/** Two triangles meeting at the seat: the conventional valve bowtie. */
function PinchValve({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} />
      <path d={`M ${fmt(-w * 0.6)} ${fmt(-h * 0.6)} L 0 0 L ${fmt(-w * 0.6)} ${fmt(h * 0.6)} Z`} {...detail} />
      <path d={`M ${fmt(w * 0.6)} ${fmt(-h * 0.6)} L 0 0 L ${fmt(w * 0.6)} ${fmt(h * 0.6)} Z`} {...detail} />
      <line x1={0} y1={-h} x2={0} y2={0} {...detail} strokeDasharray="2 2" />
    </>
  );
}

function Chamber({ size }: GlyphProps) {
  return <Box size={size} rx={Math.min(size.width, size.height) / 4} />;
}

function Port({ size }: GlyphProps) {
  const r = Math.min(size.width, size.height) / 2;
  return (
    <>
      <circle r={r} {...body} />
      <circle r={r * 0.45} {...detail} />
    </>
  );
}

function TJunction({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} />
      <path d={`M ${fmt(-w)} 0 L ${w} 0 M 0 0 L 0 ${h}`} {...detail} />
    </>
  );
}

/**
 * A junction as a point on a rail (D32): a dot where three lines meet, with
 * stubs out to its ports so the lines run into it. Drawn in the channel colour
 * so it reads as part of the line rather than as a component. The transparent
 * box keeps the whole symbol a target for the pointer.
 */
function JunctionPoint({ size, ports }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  // A stub from the dot to each port, wherever the template puts it. Without
  // ports (a palette tile), a tee with its branch down.
  const ends = ports ?? [
    { x: -w, y: 0 },
    { x: w, y: 0 },
    { x: 0, y: h },
  ];
  return (
    <>
      <rect x={-w} y={-h} width={size.width} height={size.height} fill="transparent" data-hit="junction" />
      <path
        data-stubs="junction"
        d={ends.map((end) => `M 0 0 L ${fmt(end.x)} ${fmt(end.y)}`).join(" ")}
        fill="none"
        stroke={THEME.channel}
        strokeWidth={THEME.edgeWidth}
      />
      <circle cx={0} cy={0} r={3.5} fill={THEME.channel} />
    </>
  );
}

function YJunction({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} />
      <path d={`M ${fmt(-w)} ${fmt(-h * 0.6)} L 0 0 L ${fmt(-w)} ${fmt(h * 0.6)} M 0 0 L ${w} 0`} {...detail} />
    </>
  );
}

/** A pillar array: vertical bars across the flow. */
function Filter({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const bars = [-0.5, -0.25, 0, 0.25, 0.5].map((f) => f * w);
  return (
    <>
      <Box size={size} />
      {bars.map((x) => (
        <line key={x} x1={x} y1={-h * 0.6} x2={x} y2={h * 0.6} {...detail} />
      ))}
    </>
  );
}

/** An observation window. */
function Sensor({ size }: GlyphProps) {
  const r = Math.min(size.width, size.height) * 0.25;
  return (
    <>
      <Box size={size} />
      <circle r={r} {...detail} />
      <circle r={r * 0.35} fill={THEME.ink} />
    </>
  );
}

/**
 * A membrane across a pocket, with air leaving through it: the membrane is the
 * dashed line (porous), the arrow is gas out, and the pocket below is where
 * liquid stops.
 */
function HydrophobicVent({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const membrane = fmt(-h * 0.15);
  return (
    <>
      <Box size={size} />
      <line x1={fmt(-w * 0.75)} y1={membrane} x2={fmt(w * 0.75)} y2={membrane} {...detail} strokeWidth={1.5} strokeDasharray="2 1.5" />
      <path d={`M ${fmt(-w)} ${fmt(h * 0.45)} L ${fmt(w * 0.55)} ${fmt(h * 0.45)}`} {...detail} />
      <path
        d={`M 0 ${fmt(membrane - 2)} L 0 ${fmt(-h * 0.8)} M ${fmt(-h * 0.22)} ${fmt(-h * 0.55)} L 0 ${fmt(-h * 0.8)} L ${fmt(h * 0.22)} ${fmt(-h * 0.55)}`}
        {...detail}
      />
    </>
  );
}

/** A throat opening into a sudden widening: where the meniscus pins. */
function CapillaryBurstValve({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} />
      <path
        d={`M ${fmt(-w)} ${fmt(-h * 0.2)} L ${fmt(-w * 0.1)} ${fmt(-h * 0.2)} L ${fmt(-w * 0.1)} ${fmt(-h * 0.7)} L ${fmt(w)} ${fmt(-h * 0.7)} M ${fmt(-w)} ${fmt(h * 0.2)} L ${fmt(-w * 0.1)} ${fmt(h * 0.2)} L ${fmt(-w * 0.1)} ${fmt(h * 0.7)} L ${fmt(w)} ${fmt(h * 0.7)}`}
        {...detail}
      />
      <path d={`M ${fmt(-w * 0.1)} ${fmt(-h * 0.2)} q ${fmt(w * 0.12)} ${fmt(h * 0.2)} 0 ${fmt(h * 0.4)}`} {...detail} strokeDasharray="1.5 1" />
    </>
  );
}

/** The valve bowtie with a heat mark: single use, closed by the stake tool. */
function HeatStakeValve({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <PinchValve size={size} />
      <path
        d={`M ${fmt(-w * 0.35)} ${fmt(-h * 0.95)} q ${fmt(w * 0.1)} ${fmt(-h * 0.2)} 0 ${fmt(-h * 0.35)} M 0 ${fmt(-h * 0.95)} q ${fmt(w * 0.1)} ${fmt(-h * 0.2)} 0 ${fmt(-h * 0.35)} M ${fmt(w * 0.35)} ${fmt(-h * 0.95)} q ${fmt(w * 0.1)} ${fmt(-h * 0.2)} 0 ${fmt(-h * 0.35)}`}
        {...detail}
      />
    </>
  );
}

/** A line with a break in it: a seal that opens once. */
function FrangibleSeal({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} />
      <path d={`M ${fmt(-w)} 0 L ${fmt(-w * 0.2)} 0 M ${fmt(w * 0.2)} 0 L ${fmt(w)} 0`} {...detail} />
      <path d={`M ${fmt(-w * 0.2)} ${fmt(-h * 0.5)} L 0 0 L ${fmt(-w * 0.2)} ${fmt(h * 0.5)} M ${fmt(w * 0.2)} ${fmt(-h * 0.5)} L 0 0 L ${fmt(w * 0.2)} ${fmt(h * 0.5)}`} {...detail} strokeDasharray="2 1.5" />
    </>
  );
}

/** A domed pocket over the outlet, with the plunger above: a blister. */
function Blister({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} />
      <path d={`M ${fmt(-w * 0.7)} ${fmt(h * 0.5)} Q 0 ${fmt(-h * 0.9)} ${fmt(w * 0.7)} ${fmt(h * 0.5)} Z`} {...detail} />
      <path d={`M ${fmt(-w * 0.35)} ${fmt(h * 0.25)} l ${fmt(w * 0.15)} ${fmt(-h * 0.15)} M 0 ${fmt(h * 0.25)} l ${fmt(w * 0.15)} ${fmt(-h * 0.15)}`} {...detail} />
    </>
  );
}

/** A chamber with a flexing roof and a flow arrow: a displacement pump. */
function DiaphragmPump({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} />
      <path d={`M ${fmt(-w * 0.6)} ${fmt(-h * 0.3)} Q 0 ${fmt(h * 0.3)} ${fmt(w * 0.6)} ${fmt(-h * 0.3)}`} {...detail} />
      <path d={`M ${fmt(-w * 0.5)} ${fmt(h * 0.5)} L ${fmt(w * 0.5)} ${fmt(h * 0.5)} M ${fmt(w * 0.3)} ${fmt(h * 0.3)} L ${fmt(w * 0.5)} ${fmt(h * 0.5)} L ${fmt(w * 0.3)} ${fmt(h * 0.7)}`} {...detail} />
    </>
  );
}

/**
 * A variable-volume chamber (D31): the cavity, the diaphragm across its top
 * drawn flexed into it, and a stroke arrow for the displacement.
 */
function DiaphragmChamber({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} rx={6} />
      <path d={`M ${fmt(-w)} ${fmt(-h * 0.45)} Q 0 ${fmt(h * 0.25)} ${w} ${fmt(-h * 0.45)}`} {...detail} strokeWidth={1.5} />
      <path
        d={`M 0 ${fmt(h * 0.3)} L 0 ${fmt(h * 0.8)} M ${fmt(-w * 0.12)} ${fmt(h * 0.45)} L 0 ${fmt(h * 0.3)} L ${fmt(w * 0.12)} ${fmt(h * 0.45)} M ${fmt(-w * 0.12)} ${fmt(h * 0.65)} L 0 ${fmt(h * 0.8)} L ${fmt(w * 0.12)} ${fmt(h * 0.65)}`}
        {...detail}
      />
    </>
  );
}

function Block({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} rx={4} />
      <rect x={-w + 3} y={-h + 3} width={size.width - 6} height={size.height - 6} rx={2} {...detail} />
    </>
  );
}

/** Drawn when the template is unresolvable, so the node is visibly suspect. */
function Unknown({ size }: GlyphProps) {
  return (
    <>
      <Box size={size} dashed />
      <text
        textAnchor="middle"
        dominantBaseline="central"
        fontFamily={THEME.fontFamily}
        fontSize={THEME.fontSize + 2}
        fill={THEME.muted}
      >
        ?
      </text>
    </>
  );
}

// --- Tubing-set parts (D34) ---------------------------------------------------

/**
 * Flow through a filter: a filled arrowhead just inside each end, pointing
 * from inlet (west) to outlet (east), so which end is which reads without
 * the port list.
 */
function FlowArrows({ w, at = 0.78, half }: { w: number; at?: number; half: number }) {
  const head = (x: number) => `M ${fmt(x - half * 1.5)} ${fmt(-half)} L ${fmt(x)} 0 L ${fmt(x - half * 1.5)} ${fmt(half)} Z`;
  return <path d={`${head(-w * at + half * 1.5)} ${head(w * at)}`} fill={THEME.ink} stroke="none" />;
}

/**
 * A pleated membrane in a capsule: inlet (west) to outlet (east) through a
 * pleated membrane, and the vent (north) on the upstream side, where air
 * collects before the membrane.
 */
function CapsuleFilter({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const steps = 6;
  // The membrane stands east of centre, so the vent above the centre is upstream of it.
  const x = w * 0.32;
  const zigzag = Array.from({ length: steps + 1 }, (_, i) => {
    const y = -h * 0.72 + (1.44 * h * i) / steps;
    return `${i === 0 ? "M" : "L"} ${fmt(x + (i % 2 === 0 ? -w * 0.12 : w * 0.12))} ${fmt(y)}`;
  }).join(" ");
  return (
    <>
      <Box size={size} rx={Math.min(size.width, size.height) / 3} />
      <path d={zigzag} {...detail} />
      <line x1={0} y1={fmt(-h)} x2={0} y2={fmt(-h * 0.5)} {...detail} />
      <FlowArrows w={w} half={Math.min(size.width, size.height) * 0.07} />
    </>
  );
}

/** A disk filter seen edge on: a disc with the membrane across its middle, flow west to east. */
function DiskFilter({ size }: GlyphProps) {
  const r = Math.min(size.width, size.height) / 2;
  return (
    <>
      <circle r={r} {...body} />
      <line x1={0} y1={-r * 0.8} x2={0} y2={r * 0.8} {...detail} strokeDasharray="2 1.5" />
      <FlowArrows w={r} at={0.72} half={r * 0.13} />
    </>
  );
}

/**
 * A disk filter with a vent on its upstream housing: the disc, the membrane,
 * and a short stub from the upstream (west) half up to the vent port.
 */
function DiskFilterVented({ size }: GlyphProps) {
  const r = Math.min(size.width, size.height) / 2;
  const h = size.height / 2;
  const x = -size.width / 4;
  // Where the stub leaves the disc, directly below the port.
  const leaves = -Math.sqrt(Math.max(0, r * r - x * x));
  return (
    <>
      <DiskFilter size={size} />
      <line x1={fmt(x)} y1={fmt(leaves)} x2={fmt(x)} y2={fmt(-h)} {...body} />
    </>
  );
}

/** One half of a sterile connector: a body with a mating face on its east side. */
function AsepticConnector({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <rect x={-w} y={-h} width={fmt(w * 1.35)} height={size.height} rx={2} {...body} />
      <path
        d={`M ${fmt(w * 0.35)} ${fmt(-h)} L ${fmt(w)} ${fmt(-h * 0.5)} L ${fmt(w)} ${fmt(h * 0.5)} L ${fmt(w * 0.35)} ${fmt(h)}`}
        {...body}
      />
      <line x1={fmt(w * 0.35)} y1={-h} x2={fmt(w * 0.35)} y2={h} {...detail} strokeDasharray="2 1.5" />
    </>
  );
}

/** A clamp closing on a tube: two jaws on a line. */
function PinchClamp({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <line x1={-w} y1={0} x2={w} y2={0} {...detail} />
      <path d={`M ${fmt(-w * 0.5)} ${fmt(-h)} L 0 ${fmt(-h * 0.2)} L ${fmt(w * 0.5)} ${fmt(-h)}`} {...body} />
      <path d={`M ${fmt(-w * 0.5)} ${fmt(h)} L 0 ${fmt(h * 0.2)} L ${fmt(w * 0.5)} ${fmt(h)}`} {...body} />
    </>
  );
}

/**
 * A crimp-and-cut sleeve on a tube, as a Novaseal: a metal cylinder threaded
 * on the tube, seen side on with its near end showing. The dashed line across
 * its middle is where the tool crimps it, sealing both halves and cutting
 * between them.
 */
function BandClamp({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const half = w * 0.6;
  const r = h * 0.6;
  const end = r * 0.35;
  return (
    <>
      <line x1={-w} y1={0} x2={w} y2={0} {...detail} />
      <path
        d={`M ${fmt(-half)} ${fmt(-r)} L ${fmt(half)} ${fmt(-r)} A ${fmt(end)} ${fmt(r)} 0 0 1 ${fmt(half)} ${fmt(r)} L ${fmt(-half)} ${fmt(r)} A ${fmt(end)} ${fmt(r)} 0 0 1 ${fmt(-half)} ${fmt(-r)} Z`}
        {...body}
      />
      <ellipse cx={fmt(-half)} cy={0} rx={fmt(end)} ry={fmt(r)} {...detail} />
      <line x1={0} y1={-h} x2={0} y2={h} strokeDasharray="2 1.5" {...detail} />
    </>
  );
}

/** A check valve: the ISO triangle-and-bar, flow west to east. */
function CheckValve({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <Box size={size} />
      <path d={`M ${fmt(-w * 0.45)} ${fmt(-h * 0.55)} L ${fmt(w * 0.3)} 0 L ${fmt(-w * 0.45)} ${fmt(h * 0.55)} Z`} {...detail} />
      <line x1={fmt(w * 0.3)} y1={fmt(-h * 0.55)} x2={fmt(w * 0.3)} y2={fmt(h * 0.55)} {...detail} />
    </>
  );
}

/** A flexible bag, ports at the bottom. */
function Bag({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  return (
    <>
      <path
        d={`M ${fmt(-w * 0.8)} ${fmt(-h)} L ${fmt(w * 0.8)} ${fmt(-h)} Q ${w} 0 ${fmt(w * 0.8)} ${h} L ${fmt(-w * 0.8)} ${h} Q ${-w} 0 ${fmt(-w * 0.8)} ${fmt(-h)} Z`}
        {...body}
      />
      <circle cx={0} cy={fmt(-h * 0.75)} r={fmt(Math.min(w, h) * 0.12)} {...detail} />
    </>
  );
}

/** A peristaltic pump head: a circle with its rollers. */
function PeristalticPump({ size }: GlyphProps) {
  const r = Math.min(size.width, size.height) / 2;
  return (
    <>
      <circle r={r} {...body} />
      {[0, 120, 240].map((angle) => {
        const rad = (angle * Math.PI) / 180;
        return <circle key={angle} cx={fmt(Math.cos(rad) * r * 0.55)} cy={fmt(Math.sin(rad) * r * 0.55)} r={fmt(r * 0.18)} {...detail} />;
      })}
    </>
  );
}

/**
 * An in-line sensor: the flow path through a box, with the measured quantity
 * as a letter, instrument style (F for flow, P for pressure, pH). The letter
 * turns with the symbol.
 */
function InlineSensor({ size, letter, mirrored }: GlyphProps & { letter: string }) {
  const h = size.height / 2;
  const r = Math.min(size.width, size.height) * 0.3;
  return (
    <>
      <Box size={size} />
      <circle r={r} {...detail} />
      <text
        y={fmt(r * 0.38)}
        transform={readable(mirrored)}
        textAnchor="middle"
        // Two letters (pH) are set smaller, to stay inside the ring.
        fontSize={fmt(r * (letter.length > 1 ? 0.8 : 1.1))}
        fontWeight={600}
        fill={THEME.ink}
        style={{ pointerEvents: "none" }}
      >
        {letter}
      </text>
      <line x1={0} y1={-h} x2={0} y2={fmt(-r)} {...detail} />
    </>
  );
}

/**
 * A clamp-on sensor: two jaws closed round the tube, which runs straight
 * through. Nothing of the sensor is in the flow path, and the drawing says so.
 * The letter sits in the lower jaw, clear of the signal port at the top.
 */
function ClampOnSensor({ size, letter, mirrored }: GlyphProps & { letter: string }) {
  const w = size.width / 2;
  const h = size.height / 2;
  const jaw = w * 0.6;
  return (
    <>
      <line x1={-w} y1={0} x2={w} y2={0} stroke={THEME.channel} strokeWidth={2} />
      <path d={`M ${fmt(-jaw)} ${fmt(-h * 0.22)} L ${fmt(-jaw)} ${fmt(-h * 0.7)} L ${fmt(jaw)} ${fmt(-h * 0.7)} L ${fmt(jaw)} ${fmt(-h * 0.22)}`} {...body} />
      <path d={`M ${fmt(-jaw)} ${fmt(h * 0.22)} L ${fmt(-jaw)} ${h} L ${fmt(jaw)} ${h} L ${fmt(jaw)} ${fmt(h * 0.22)}`} {...body} />
      <text
        y={fmt(h * 0.61)}
        transform={readable(mirrored)}
        textAnchor="middle"
        dominantBaseline="central"
        fontSize={fmt(h * 0.72)}
        fontWeight={700}
        fill={THEME.ink}
        style={{ pointerEvents: "none" }}
      >
        {letter}
      </text>
    </>
  );
}

/**
 * An instrument's pinch valve on a tube: the tube runs straight through,
 * a plunger with a wedge tip presses on it from the actuator above, and an
 * anvil takes it from below. The actuator is the ISO solenoid, a box with a
 * diagonal. Drawn so it reads as clamped on, like the clamp-on sensors.
 */
function PinchValveActuator({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const coil = { x: w * 0.5, top: -h, bottom: -h * 0.46 };
  const tip = -h * 0.12;
  const wedge = w * 0.22;
  return (
    <>
      <line x1={-w} y1={0} x2={w} y2={0} stroke={THEME.channel} strokeWidth={2} />
      <rect
        x={fmt(-coil.x)}
        y={fmt(coil.top)}
        width={fmt(2 * coil.x)}
        height={fmt(coil.bottom - coil.top)}
        {...body}
      />
      <line x1={fmt(-coil.x)} y1={fmt(coil.bottom)} x2={fmt(coil.x)} y2={fmt(coil.top)} {...detail} />
      <line x1={0} y1={fmt(coil.bottom)} x2={0} y2={fmt(-h * 0.3)} {...detail} />
      <path d={`M ${fmt(-wedge)} ${fmt(-h * 0.3)} L ${fmt(wedge)} ${fmt(-h * 0.3)} L 0 ${fmt(tip)} Z`} {...body} />
      <rect x={fmt(-w * 0.55)} y={fmt(h * 0.14)} width={fmt(w * 1.1)} height={fmt(h * 0.26)} {...body} />
    </>
  );
}

/**
 * An electro-pneumatic pressure regulator (D65), after ISO 1219: the valve
 * body with the flow arrow from supply (west) to outlet (east), crossed by the
 * diagonal arrow of an adjustable setting, and above it the proportional
 * solenoid (a box with a diagonal and an arrow) that the control signal
 * drives, at the control port on top.
 */
function PressureRegulator({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const box = { x: w * 0.62, top: -h * 0.44, bottom: h * 0.84 };
  const coil = { x: w * 0.42, top: -h, bottom: -h * 0.66 };
  const flow = w * 0.42;
  const head = w * 0.14;
  const diagonal = { x0: -box.x * 0.6, y0: box.bottom - h * 0.12, x1: box.x * 0.6, y1: box.top + h * 0.12 };
  const angle = Math.atan2(diagonal.y1 - diagonal.y0, diagonal.x1 - diagonal.x0);
  const tip = (x: number, y: number, a: number) =>
    `M ${fmt(x)} ${fmt(y)} L ${fmt(x - head * Math.cos(a - 0.45))} ${fmt(y - head * Math.sin(a - 0.45))} ` +
    `L ${fmt(x - head * Math.cos(a + 0.45))} ${fmt(y - head * Math.sin(a + 0.45))} Z`;
  return (
    <>
      <line x1={-w} y1={0} x2={fmt(-box.x)} y2={0} {...detail} />
      <line x1={fmt(box.x)} y1={0} x2={w} y2={0} {...detail} />
      <rect x={fmt(-box.x)} y={fmt(box.top)} width={fmt(2 * box.x)} height={fmt(box.bottom - box.top)} {...body} />
      <line x1={fmt(-flow)} y1={0} x2={fmt(flow - head)} y2={0} {...detail} />
      <path d={tip(flow, 0, 0)} fill={THEME.ink} stroke="none" />
      <line x1={fmt(diagonal.x0)} y1={fmt(diagonal.y0)} x2={fmt(diagonal.x1)} y2={fmt(diagonal.y1)} {...detail} />
      <path d={tip(diagonal.x1, diagonal.y1, angle)} fill={THEME.ink} stroke="none" />
      <line x1={0} y1={fmt(coil.bottom)} x2={0} y2={fmt(box.top)} {...detail} />
      <rect x={fmt(-coil.x)} y={fmt(coil.top)} width={fmt(2 * coil.x)} height={fmt(coil.bottom - coil.top)} {...body} />
      <line x1={fmt(-coil.x)} y1={fmt(coil.bottom)} x2={fmt(coil.x)} y2={fmt(coil.top)} {...detail} />
    </>
  );
}

/**
 * An instrument's air supply (D66), after ISO 1219: a circle with a triangle,
 * solid pointing out at the outlet (east) for pressure, pointing in for vacuum.
 */
function AirSupply({ size, vacuum = false }: GlyphProps & { vacuum?: boolean }) {
  const w = size.width / 2;
  const h = size.height / 2;
  const r = Math.min(w, h) * 0.72;
  const cx = -w + r + 1;
  const t = r * 0.55;
  const tip = vacuum ? cx - t * 0.7 : cx + t * 0.9;
  const base = vacuum ? cx + t * 0.6 : cx - t * 0.5;
  return (
    <>
      <line x1={fmt(cx + r)} y1={0} x2={w} y2={0} {...detail} />
      <circle cx={fmt(cx)} cy={0} r={fmt(r)} {...body} />
      <path d={`M ${fmt(tip)} 0 L ${fmt(base)} ${fmt(-t * 0.7)} L ${fmt(base)} ${fmt(t * 0.7)} Z`} fill={THEME.ink} stroke="none" />
    </>
  );
}

/** A vent to atmosphere (D66), after ISO 1219's exhaust: the line ending in an open triangle at the air. */
function AtmosphereVent({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const t = Math.min(w, h) * 0.6;
  return (
    <>
      <line x1={-w} y1={0} x2={fmt(-t * 0.3)} y2={0} {...detail} />
      <path d={`M ${fmt(-t * 0.3)} ${fmt(-t)} L ${fmt(t * 0.9)} 0 L ${fmt(-t * 0.3)} ${fmt(t)} Z`} {...body} />
      <line x1={fmt(t * 0.9)} y1={0} x2={w} y2={0} {...detail} strokeDasharray="2 2" />
    </>
  );
}

/**
 * A 3/2-way solenoid valve (D66), after ISO 1219: two positions side by side.
 * The right one is where the spring holds it, common (east) to exhaust; the
 * left, where the solenoid on top pulls it, supply to common. The supply
 * enters at the upper left and the exhaust leaves at the lower left.
 */
function solenoidGeometry(size: Size) {
  const w = size.width / 2;
  const h = size.height / 2;
  const box = { left: -w * 0.62, right: w * 0.7, top: -h * 0.36, bottom: h * 0.62 };
  const mid = (box.left + box.right) / 2;
  const ys = { supply: -h * 0.5, exhaust: h * 0.5 };
  const inset = (box.bottom - box.top) * 0.22;
  return {
    w,
    h,
    box,
    mid,
    ys,
    inset,
    stubs: {
      supply: `M ${-w} ${fmt(ys.supply)} L ${fmt(box.left + inset)} ${fmt(ys.supply)} L ${fmt(box.left + inset)} ${fmt(box.top)}`,
      exhaust: `M ${-w} ${fmt(ys.exhaust)} L ${fmt(box.left - w * 0.1)} ${fmt(ys.exhaust)}`,
      common: `M ${fmt(box.right)} 0 L ${w} 0`,
    },
  };
}

/**
 * Where a 3/2 solenoid's state puts it, in the glyph's own frame: the
 * position it is in (the left one energised to supply, the right at rest to
 * exhaust), the one it is not, and the stubs of the ports joined. Null for
 * any other glyph or state. Drawn over the glyph by a script's preview.
 */
export function switchedRoute(
  glyph: string,
  size: Size,
  port: string,
): { active: { x: number; y: number; width: number; height: number }; idle: { x: number; y: number; width: number; height: number }; stubs: string[] } | null {
  if (glyph !== "solenoid_valve_3_2" || (port !== "supply" && port !== "exhaust")) return null;
  const { box, mid, stubs } = solenoidGeometry(size);
  const left = { x: fmt(box.left), y: fmt(box.top), width: fmt(mid - box.left), height: fmt(box.bottom - box.top) };
  const right = { x: fmt(mid), y: fmt(box.top), width: fmt(box.right - mid), height: fmt(box.bottom - box.top) };
  return port === "supply"
    ? { active: left, idle: right, stubs: [stubs.supply, stubs.common] }
    : { active: right, idle: left, stubs: [stubs.exhaust, stubs.common] };
}

function SolenoidValve32({ size }: GlyphProps) {
  const { w, h, box, mid, ys, inset, stubs } = solenoidGeometry(size);
  const head = w * 0.1;
  const arrow = (x1: number, y1: number, x2: number, y2: number) => {
    const a = Math.atan2(y2 - y1, x2 - x1);
    return (
      <>
        <line x1={fmt(x1)} y1={fmt(y1)} x2={fmt(x2)} y2={fmt(y2)} {...detail} />
        <path
          d={`M ${fmt(x2)} ${fmt(y2)} L ${fmt(x2 - head * Math.cos(a - 0.5))} ${fmt(y2 - head * Math.sin(a - 0.5))} L ${fmt(x2 - head * Math.cos(a + 0.5))} ${fmt(y2 - head * Math.sin(a + 0.5))} Z`}
          fill={THEME.ink}
          stroke="none"
        />
      </>
    );
  };
  return (
    <>
      {/* Port stubs: supply and exhaust on the west, common on the east. */}
      <path d={stubs.supply} {...detail} />
      <path d={stubs.exhaust} {...detail} />
      <path d={stubs.common} {...detail} />
      <rect x={fmt(box.left)} y={fmt(box.top)} width={fmt(box.right - box.left)} height={fmt(box.bottom - box.top)} {...body} />
      <line x1={fmt(mid)} y1={fmt(box.top)} x2={fmt(mid)} y2={fmt(box.bottom)} {...detail} />
      {/* Energised: supply through to common. */}
      {arrow(box.left + inset, box.bottom - inset, mid - inset, box.top + inset)}
      {/* At rest: common through to exhaust; the supply blocked. */}
      {arrow(box.right - inset, box.top + inset, mid + inset, box.bottom - inset)}
      <path d={`M ${fmt(box.right - inset * 1.4)} ${fmt(box.bottom - inset)} L ${fmt(box.right - inset * 0.6)} ${fmt(box.bottom - inset)}`} {...detail} />
      <line x1={fmt(box.right - inset)} y1={fmt(box.bottom - inset)} x2={fmt(box.right - inset)} y2={fmt(box.bottom - inset * 1.6)} {...detail} />
      {/* The exhaust's triangle, out of the lower left. */}
      <path d={`M ${fmt(box.left - w * 0.1)} ${fmt(ys.exhaust - h * 0.14)} L ${fmt(box.left - w * 0.1)} ${fmt(ys.exhaust + h * 0.14)} L ${fmt(box.left - w * 0.28)} ${fmt(ys.exhaust)} Z`} {...body} />
      {/* The solenoid, on top: a box with a diagonal, to the coil port. */}
      <line x1={0} y1={-h} x2={0} y2={fmt(-h * 0.84)} {...detail} />
      <rect x={fmt(-w * 0.26)} y={fmt(-h * 0.84)} width={fmt(w * 0.52)} height={fmt(h * 0.4)} {...body} />
      <line x1={fmt(-w * 0.26)} y1={fmt(-h * 0.44)} x2={fmt(w * 0.26)} y2={fmt(-h * 0.84)} {...detail} />
    </>
  );
}

/**
 * An instrument's peristaltic pump head on the set's pump tubing (D48): the
 * tube runs straight through, the rotor with its rollers presses on it from
 * above, and the track takes it from below. The arrowhead on the tube is the
 * pumping direction, inlet to outlet, so a head mounted on a tube shows which
 * way it pumps. Drawn clamped on, like the pinch valve and clamp-on sensors.
 */
function PeristalticPumpHead({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const rotor = { cy: -h * 0.44, r: h * 0.5 };
  const arrow = { x: w * 0.82, half: h * 0.14 };
  return (
    <>
      <line x1={-w} y1={0} x2={w} y2={0} stroke={THEME.channel} strokeWidth={2} />
      <path
        d={`M ${fmt(arrow.x - arrow.half * 1.6)} ${fmt(-arrow.half)} L ${fmt(arrow.x)} 0 L ${fmt(arrow.x - arrow.half * 1.6)} ${fmt(arrow.half)}`}
        {...detail}
      />
      <circle cy={fmt(rotor.cy)} r={fmt(rotor.r)} {...body} />
      {[90, 210, 330].map((angle) => {
        const rad = (angle * Math.PI) / 180;
        return (
          <circle
            key={angle}
            cx={fmt(Math.cos(rad) * rotor.r * 0.6)}
            cy={fmt(rotor.cy + Math.sin(rad) * rotor.r * 0.6)}
            r={fmt(rotor.r * 0.22)}
            {...detail}
          />
        );
      })}
      <rect x={fmt(-w * 0.6)} y={fmt(h * 0.14)} width={fmt(w * 1.2)} height={fmt(h * 0.26)} {...body} />
    </>
  );
}

/**
 * A rigid staging vessel on a tubing set (D50): a hexagon, flat at the top and
 * bottom where its ports sit, pointed at the sides. The dashed line is a
 * liquid level, so it reads as a vessel rather than a part.
 */
function ReservoirVessel({ size, filled }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  // The flat edges reach past the ports at a quarter of the width either side.
  const flat = w * 0.62;
  const level = h * 0.2;
  const across = flat + ((w - flat) * (h - level)) / h;
  return (
    <>
      <path
        d={`M ${fmt(-flat)} ${fmt(-h)} L ${fmt(flat)} ${fmt(-h)} L ${w} 0 L ${fmt(flat)} ${h} L ${fmt(-flat)} ${h} L ${-w} 0 Z`}
        {...body}
      />
      {!filled && <line x1={fmt(-across)} y1={fmt(level)} x2={fmt(across)} y2={fmt(level)} strokeDasharray="3 2" {...detail} />}
    </>
  );
}

/**
 * A tangential-flow filter (D51): a long module, feed along it end to end over
 * the dashed membrane, permeate on the shell below. The arrow is the sweep
 * that makes it tangential.
 */
function TffCartridge({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const membrane = h * 0.25;
  const flow = -h * 0.3;
  const tip = w * 0.45;
  return (
    <>
      <Box size={size} rx={Math.min(size.width, size.height) / 4} />
      <line x1={fmt(-w * 0.85)} y1={fmt(membrane)} x2={fmt(w * 0.85)} y2={fmt(membrane)} strokeDasharray="3 2" {...detail} />
      <path
        d={`M ${fmt(-tip)} ${fmt(flow)} L ${fmt(tip)} ${fmt(flow)} M ${fmt(tip - 4)} ${fmt(flow - 3)} L ${fmt(tip)} ${fmt(flow)} L ${fmt(tip - 4)} ${fmt(flow + 3)}`}
        {...detail}
      />
    </>
  );
}

/** Barbs pointing out along a tube, at one end of a coupling. */
function barbs(end: number, inward: number, half: number): string {
  // Two ridges, each a chevron opening towards the part's body.
  const step = inward * 0.45;
  return [0, 1]
    .map((index) => {
      const x = end + step * index;
      return `M ${fmt(x + step)} ${fmt(-half)} L ${fmt(x)} 0 L ${fmt(x + step)} ${fmt(half)}`;
    })
    .join(" ");
}

/** A straight barbed coupling: a short body, barbed at each end, the tube running through. */
function Coupling({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const collar = w * 0.28;
  return (
    <>
      <line x1={-w} y1={0} x2={w} y2={0} {...detail} />
      <path d={barbs(-w, (w - collar) * 0.7, h * 0.62)} {...detail} />
      <path d={barbs(w, -(w - collar) * 0.7, h * 0.62)} {...detail} />
      <rect x={fmt(-collar)} y={fmt(-h)} width={fmt(2 * collar)} height={fmt(2 * h)} rx={1.5} {...body} />
    </>
  );
}

/**
 * A reducing coupling: barbs of two sizes and a tapered body, wide at end A
 * (west) and narrow at end B (east). Flipping it turns it round.
 */
function ReducingCoupling({ size }: GlyphProps) {
  const w = size.width / 2;
  const h = size.height / 2;
  const collar = w * 0.32;
  const narrow = h * 0.55;
  return (
    <>
      <line x1={-w} y1={0} x2={w} y2={0} {...detail} />
      <path d={barbs(-w, (w - collar) * 0.7, h * 0.7)} {...detail} />
      <path d={barbs(w, -(w - collar) * 0.7, narrow * 0.7)} {...detail} />
      <path
        d={`M ${fmt(-collar)} ${fmt(-h)} L ${fmt(collar)} ${fmt(-narrow)} L ${fmt(collar)} ${fmt(narrow)} L ${fmt(-collar)} ${fmt(h)} Z`}
        {...body}
      />
    </>
  );
}

const GLYPHS: Record<string, (props: GlyphProps) => ReactElement> = {
  pinch_valve_actuator: PinchValveActuator,
  pressure_regulator: PressureRegulator,
  pressure_supply: AirSupply,
  vacuum_supply: (props) => <AirSupply {...props} vacuum />,
  atmosphere_vent: AtmosphereVent,
  solenoid_valve_3_2: SolenoidValve32,
  peristaltic_pump_head: PeristalticPumpHead,
  reservoir_vessel: ReservoirVessel,
  tff_cartridge: TffCartridge,
  coupling: Coupling,
  reducing_coupling: ReducingCoupling,
  flow_sensor: (props) => <InlineSensor {...props} letter="F" />,
  pressure_sensor: (props) => <InlineSensor {...props} letter="P" />,
  clamp_flow_sensor: (props) => <ClampOnSensor {...props} letter="F" />,
  clamp_pressure_sensor: (props) => <ClampOnSensor {...props} letter="P" />,
  bubble_sensor: (props) => <ClampOnSensor {...props} letter="B" />,
  clamp_conductivity_sensor: (props) => <ClampOnSensor {...props} letter="C" />,
  ph_sensor: (props) => <InlineSensor {...props} letter="pH" />,
  conductivity_sensor: (props) => <InlineSensor {...props} letter="C" />,
  uv_flow_cell: (props) => <InlineSensor {...props} letter="UV" />,
  capsule_filter: CapsuleFilter,
  disk_filter: DiskFilter,
  disk_filter_vented: DiskFilterVented,
  aseptic_connector: AsepticConnector,
  pinch_clamp: PinchClamp,
  band_clamp: BandClamp,
  check_valve: CheckValve,
  bag: Bag,
  peristaltic_pump: PeristalticPump,
  pinch_valve: PinchValve,
  chamber: Chamber,
  port: Port,
  junction: JunctionPoint,
  t_junction: TJunction,
  y_junction: YJunction,
  filter: Filter,
  sensor: Sensor,
  hydrophobic_vent: HydrophobicVent,
  capillary_burst_valve: CapillaryBurstValve,
  heat_stake_valve: HeatStakeValve,
  frangible_seal: FrangibleSeal,
  blister: Blister,
  diaphragm_pump: DiaphragmPump,
  diaphragm_chamber: DiaphragmChamber,
  block: Block,
  unknown: Unknown,
};

export function Glyph({ name, size, ports, filled, mirrored }: { name: string; size: Size; ports?: GlyphProps["ports"]; filled?: boolean; mirrored?: boolean }) {
  // A glyph name no renderer knows falls back visibly rather than vanishing.
  const Component = GLYPHS[name] ?? Unknown;
  return <Component size={size} ports={ports} filled={filled} mirrored={mirrored} />;
}

export function hasGlyph(name: string): boolean {
  return name in GLYPHS;
}

/** A serpentine mark: this channel is much longer than its line. */
export function SerpentineMark({ x, y }: { x: number; y: number }) {
  return (
    <path
      d={`M ${fmt(x - 7)} ${fmt(y + 3)} q 1.75 -6 3.5 0 t 3.5 0 t 3.5 0 t 3.5 0`}
      fill="none"
      stroke={THEME.channel}
      strokeWidth={1.25}
      data-mark="serpentine"
    />
  );
}

/** A herringbone mark: this channel mixes. */
export function MixingMark({ x, y }: { x: number; y: number }) {
  return (
    <path
      d={`M ${fmt(x - 7)} ${fmt(y - 3)} l 3 3 l -3 3 M ${fmt(x - 2)} ${fmt(y - 3)} l 3 3 l -3 3 M ${fmt(x + 3)} ${fmt(y - 3)} l 3 3 l -3 3`}
      fill="none"
      stroke={THEME.channel}
      strokeWidth={1.25}
      data-mark="mixing"
    />
  );
}
